import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field", "error", "submit"]
  static values = { 
    validateOnBlur: { type: Boolean, default: true },
    validateOnInput: { type: Boolean, default: false },
    showSuccessStates: { type: Boolean, default: true }
  }

  connect() {
    this.validationRules = this.loadValidationRules()
    this.setupEventListeners()
  }

  setupEventListeners() {
    this.fieldTargets.forEach(field => {
      if (this.validateOnBlurValue) {
        field.addEventListener('blur', this.validateSingleField.bind(this))
      }
      
      if (this.validateOnInputValue) {
        field.addEventListener('input', this.debounce(this.validateSingleField.bind(this), 300))
      }
    })
  }

  // Validate single field
  validateSingleField(event) {
    const field = event.target
    const fieldName = field.name
    const value = field.value.trim()
    
    // Clear previous validation state
    this.clearFieldValidation(field)
    
    // Get validation rules for this field
    const rules = this.validationRules[fieldName] || []
    
    // Run validation rules
    for (const rule of rules) {
      const result = this.runValidationRule(rule, value, field)
      if (!result.valid) {
        this.showFieldError(field, result.message)
        return false
      }
    }
    
    // Show success state if enabled
    if (this.showSuccessStatesValue && value) {
      this.showFieldSuccess(field)
    }
    
    return true
  }

  // Validate entire form
  validateForm(event) {
    let isValid = true
    let firstErrorField = null
    
    this.fieldTargets.forEach(field => {
      const fieldValid = this.validateSingleField({ target: field })
      if (!fieldValid && !firstErrorField) {
        firstErrorField = field
      }
      isValid = isValid && fieldValid
    })
    
    if (!isValid) {
      event.preventDefault()
      if (firstErrorField) {
        firstErrorField.focus()
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
    
    return isValid
  }

  // Run individual validation rule
  runValidationRule(rule, value, field) {
    switch (rule.type) {
      case 'required':
        return {
          valid: value.length > 0,
          message: rule.message || 'This field is required'
        }
      
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return {
          valid: !value || emailRegex.test(value),
          message: rule.message || 'Please enter a valid email address'
        }
      
      case 'minLength':
        return {
          valid: !value || value.length >= rule.value,
          message: rule.message || `Must be at least ${rule.value} characters`
        }
      
      case 'maxLength':
        return {
          valid: !value || value.length <= rule.value,
          message: rule.message || `Must be no more than ${rule.value} characters`
        }
      
      case 'pattern':
        const regex = new RegExp(rule.value)
        return {
          valid: !value || regex.test(value),
          message: rule.message || 'Invalid format'
        }
      
      case 'number':
        const numValue = parseFloat(value)
        return {
          valid: !value || !isNaN(numValue),
          message: rule.message || 'Must be a valid number'
        }
      
      case 'min':
        const minValue = parseFloat(value)
        return {
          valid: !value || (minValue >= rule.value),
          message: rule.message || `Must be at least ${rule.value}`
        }
      
      case 'max':
        const maxValue = parseFloat(value)
        return {
          valid: !value || (maxValue <= rule.value),
          message: rule.message || `Must be no more than ${rule.value}`
        }
      
      case 'confirmation':
        const confirmField = this.element.querySelector(`[name="${rule.field}"]`)
        return {
          valid: !value || value === confirmField.value,
          message: rule.message || 'Confirmation does not match'
        }
      
      case 'custom':
        return rule.validator(value, field)
      
      default:
        return { valid: true }
    }
  }

  // Show field error
  showFieldError(field, message) {
    field.classList.add('field-input--error')
    field.classList.remove('field-input--success')
    
    // Create error element
    const errorElement = document.createElement('span')
    errorElement.className = 'error-message'
    errorElement.textContent = message
    errorElement.setAttribute('role', 'alert')
    errorElement.setAttribute('data-form-validation-target', 'error')
    
    // Insert error message
    const errorId = `${field.id || field.name}-error`
    errorElement.id = errorId
    field.setAttribute('aria-describedby', errorId)
    field.setAttribute('aria-invalid', 'true')
    
    // Insert after field or after field wrapper
    const insertAfter = field.closest('.field-wrapper') || field
    insertAfter.parentNode.insertBefore(errorElement, insertAfter.nextSibling)
  }

  // Show field success
  showFieldSuccess(field) {
    field.classList.add('field-input--success')
    field.classList.remove('field-input--error')
    field.removeAttribute('aria-invalid')
  }

  // Clear field validation
  clearFieldValidation(field) {
    field.classList.remove('field-input--error', 'field-input--success')
    field.removeAttribute('aria-invalid')
    field.removeAttribute('aria-describedby')
    
    // Remove existing error messages
    const existingErrors = field.parentNode.querySelectorAll('.error-message')
    existingErrors.forEach(error => error.remove())
  }

  // Load validation rules from data attributes
  loadValidationRules() {
    const rules = {}
    
    this.fieldTargets.forEach(field => {
      const fieldName = field.name
      const fieldRules = []
      
      // Required validation
      if (field.hasAttribute('required') || field.hasAttribute('aria-required')) {
        fieldRules.push({
          type: 'required',
          message: field.getAttribute('data-required-message')
        })
      }
      
      // Email validation
      if (field.type === 'email') {
        fieldRules.push({
          type: 'email',
          message: field.getAttribute('data-email-message')
        })
      }
      
      // Length validation
      const minLength = field.getAttribute('minlength')
      if (minLength) {
        fieldRules.push({
          type: 'minLength',
          value: parseInt(minLength),
          message: field.getAttribute('data-minlength-message')
        })
      }
      
      const maxLength = field.getAttribute('maxlength')
      if (maxLength) {
        fieldRules.push({
          type: 'maxLength',
          value: parseInt(maxLength),
          message: field.getAttribute('data-maxlength-message')
        })
      }
      
      // Number validation
      if (field.type === 'number') {
        fieldRules.push({
          type: 'number',
          message: field.getAttribute('data-number-message')
        })
        
        const min = field.getAttribute('min')
        if (min) {
          fieldRules.push({
            type: 'min',
            value: parseFloat(min),
            message: field.getAttribute('data-min-message')
          })
        }
        
        const max = field.getAttribute('max')
        if (max) {
          fieldRules.push({
            type: 'max',
            value: parseFloat(max),
            message: field.getAttribute('data-max-message')
          })
        }
      }
      
      // Pattern validation
      const pattern = field.getAttribute('pattern')
      if (pattern) {
        fieldRules.push({
          type: 'pattern',
          value: pattern,
          message: field.getAttribute('data-pattern-message')
        })
      }
      
      // Custom validation
      const customValidator = field.getAttribute('data-custom-validator')
      if (customValidator && window[customValidator]) {
        fieldRules.push({
          type: 'custom',
          validator: window[customValidator]
        })
      }
      
      if (fieldRules.length > 0) {
        rules[fieldName] = fieldRules
      }
    })
    
    return rules
  }

  // Debounce helper
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  // Public method to validate form programmatically
  validate() {
    return this.validateForm({ preventDefault: () => {} })
  }

  // Public method to clear all validations
  clearValidations() {
    this.fieldTargets.forEach(field => {
      this.clearFieldValidation(field)
    })
  }
}